import React, { useEffect, useState } from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Carousel, { CarouselSlide, CarouselStyles } from "@WhiteCap/Widgets/HomePage/Carousel";
import LazyImage from "@insite/mobius/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import Link, { LinkProps } from "@insite/mobius/Link";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import translate from "@insite/client-framework/Translate";
import Search from "@WhiteCap/Icons/Search";

const enum fields {
    quickLinkItem = "quickLinkItem",
}

export interface HomeQuickLink {
    fields: {
        title: string;
        destination: LinkFieldValue;
        tab: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.quickLinkItem]: HomeQuickLink[];
    };
}

export interface HomeQuickLinkStyles {
    container?: InjectableCss;
    quick?: InjectableCss;
    branch?: InjectableCss;
    icons?: IconProps;
    links?: LinkProps;
    linkContainer?: InjectableCss;
    searchicons?: IconProps;
}

const homeQuickLinkStyle: HomeQuickLinkStyles = {
    container: {
        css: css`
            border-style: none;
            border-width: 1px;
            border-radius: 0;
            margin: 0;
            padding: 0;
            background: #fff;
        `,
    },
    quick: {
        css: css`
            font-size: 16px;
            text-transform: uppercase;
            color: #fff;
            background: #2e2e2e none repeat scroll 0% 0%;
            font-weight: 700;
            padding: 17px;
            margin-top: 0px;
            margin-bottom: 15px;
            text-align: left;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    branch: {
        css: css`
            position: relative;
            font-size: 16px;
            text-transform: uppercase;
            color: #fff;
            background: #2e2e2e none repeat scroll 0% 0%;
            font-weight: 700;
            padding: 17px;
            margin-top: -15px;
            margin-bottom: 15px;
            text-align: left;
            padding-right: 0;
            padding-left: 15px;
            display: flex;
            justify-content: center;
            gap: 6px;
        `,
    },
    icons: {
        size: 24,
        color: "#FBC300",

        css: css`
            background: transparent;
            font-weight: 900;
        `,
    },
    searchicons: {
        size: 20,
        color: "#FBC300",

        css: css`
            background: transparent;
            position: absolute;
            left: 14px;
            top: 20px;
            font-weight: 900;
            color: "#FBC300";
            -moz-transform: scale(-1, 1);
            -webkit-transform: scale(-1, 1);
            -o-transform: scale(-1, 1);
            -ms-transform: scale(-1, 1);
            transform: scale(-1, 1);
        `,
    },
    links: {
        css: css`
            width: 100%;
            ${TypographyStyle} {
                font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
                display: flex;
                justify-content: space-between;
                width: 100%;
                color: #000;
                text-decoration: none;
                font-size: 14px;
                font-weight: 600;
            }
        `,
    },
    linkContainer: {
        css: css`
            width: 100%;
            list-style: outside none none;
            font-size: 14px;
            padding: 16px 20px;
            border-bottom: 1px solid #e5e5e5;
            margin-top: 0rem;
            margin-bottom: 1rem;
        `,
    },
};

const GenrateUrl = ({ text, link }: { text: string; link: LinkFieldValue }) => {
    const url = useGetLink(link);
    return (
        <StyledWrapper {...styles.linkContainer}>
            <Link href={url.url} {...styles.links}>
                {text} <Icon src={ChevronRight} {...styles.icons} />
            </Link>
        </StyledWrapper>
    );
};

const styles = homeQuickLinkStyle;
const HomeQuickLink = ({ fields }: OwnProps) => {
    const QuickLinks = fields.quickLinkItem
        .filter(item => item.fields.tab === "quickLink")
        .map(item => <GenrateUrl text={item.fields.title} link={item.fields.destination} key={item.fields.title} />);

    const LocationLinks = fields.quickLinkItem
        .filter(item => item.fields.tab === "lookingForBranch")
        .map(item => <GenrateUrl text={item.fields.title} link={item.fields.destination} key={item.fields.title} />);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.quick}>{translate("QUICK LINKS")}</StyledWrapper>
            {QuickLinks}
            <StyledWrapper {...styles.branch}>
                <Icon src={Search} {...styles.searchicons} /> {translate("LOOKING FOR A BRANCH?")}
            </StyledWrapper>
            {LocationLinks}
        </StyledWrapper>
    );
};

const tabTypeOptions = [
    { value: "quickLink", displayName: "Quick Link" },
    { value: "lookingForBranch", displayName: "Looking for a Branch" },
];

const HomeQuickLinkModule: WidgetModule = {
    component: HomeQuickLink,
    definition: {
        displayName: "Basic Quick Link",
        fieldDefinitions: [
            {
                name: fields.quickLinkItem,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tab",
                        displayName: "Select Section",
                        editorTemplate: "DropDownField",
                        options: tabTypeOptions,
                        defaultValue: "quickLink",
                        sortOrder: 1,
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        displayName: "Link Url",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                ],
            },
        ],
        group: "Basic",
    },
};

export default HomeQuickLinkModule;
